'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight } from '@phosphor-icons/react'
import { InputSubmit } from './InputSubmit'

const EASE_OUT: [number, number, number, number] = [0, 0, 0.2, 1]
const EASE_IN_OUT: [number, number, number, number] = [0.4, 0, 0.2, 1]
const CIRC = 2 * Math.PI * 24

const HS_PORTAL_ID = '14536254'
const HS_BLOG_FORM_GUID = '81f554c8-064e-4d32-a1b4-004780b921e1'

async function submitToHubspot(email: string, ip: string): Promise<boolean> {
  try {
    const res = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${HS_BLOG_FORM_GUID}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: [
            { name: 'email',     value: email },
            { name: 'collateral_title', value: document.title },
            { name: 'collateral',       value: window.location.href },
            { name: 'collateral_type',  value: 'Stories' },
            // Omitted when empty so a blank value is never sent as a form field.
            ...(ip ? [{ name: 'ipaddress', value: ip }] : []),
          ],
          // HubSpot rejects the whole submission with INVALID_IP_ADDRESS if
          // ipAddress is present but empty — omit the key instead of sending ''.
          context: { pageUri: window.location.href, pageName: document.title, ...(ip ? { ipAddress: ip } : {}) },
        }),
      }
    )
    return res.ok
  } catch {
    return false
  }
}

function SubscribeSuccess() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--seasons-space-4)', marginTop: 'var(--seasons-space-6)' }}>
      <motion.svg width="40" height="40" viewBox="0 0 56 56" fill="none">
        <circle cx="28" cy="28" r="24" stroke="var(--seasons-color-border-default)" strokeWidth="2.5" />
        <motion.circle
          cx="28" cy="28" r="24"
          stroke="var(--seasons-color-success-600, #16a34a)" strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={CIRC}
          initial={{ strokeDashoffset: CIRC, rotate: -90 }}
          animate={{ strokeDashoffset: 0 }}
          style={{ transformOrigin: '28px 28px' }}
          transition={{ duration: 0.7, ease: EASE_IN_OUT }}
        />
        <motion.polyline
          points="18,28 25,35 38,21"
          stroke="var(--seasons-color-success-600, #16a34a)" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" fill="none"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
          transition={{ duration: 0.35, ease: EASE_OUT, delay: 0.7 }}
        />
      </motion.svg>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.85 }}
      >
        <p style={{ fontFamily: 'var(--seasons-font-polysans)', fontSize: 'var(--seasons-font-size-16)', fontWeight: 'var(--seasons-font-weight-medium)', color: 'var(--seasons-color-prose-heading)', margin: 0 }}>
          You&rsquo;re subscribed!
        </p>
        <p style={{ fontFamily: 'var(--seasons-font-polysans)', fontSize: 'var(--seasons-font-size-14)', color: 'var(--seasons-color-text-secondary)', marginTop: 'var(--seasons-space-1)' }}>
          Check your inbox to confirm your subscription.
        </p>
      </motion.div>
    </div>
  )
}

export type NewsletterVariant =
  | 'midnight-statement'
  | 'editorial-light'
  | 'split-panel'
  | 'minimal-eyebrow'
  | 'inline-ribbon'
  | 'compact-card'
  // legacy alias
  | 'banner'

interface NewsletterInlineProps {
  heading?: string
  body?: string
  variant?: NewsletterVariant
  /** Read server-side in page.tsx from the request headers — see getClientIp. */
  visitorIp?: string
}

const DEFAULT_HEADING = 'Stay connected with Innovaccer'
const DEFAULT_BODY =
  'Subscribe to receive the latest insights, updates, and stories from healthcare innovation.'

export function NewsletterInline({
  heading = DEFAULT_HEADING,
  body = DEFAULT_BODY,
  variant = 'midnight-statement',
  visitorIp = '',
}: NewsletterInlineProps) {
  const v: NewsletterVariant = variant === 'banner' ? 'midnight-statement' : variant

  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [hiddenFields, setHiddenFields] = useState({ pageName: '', pageURI: '', pageID: '', ipAddress: '', hutk: '' })

  useEffect(() => {
    const hutk = document.cookie.split('; ').find(r => r.startsWith('hubspotutk='))?.split('=')[1] ?? ''
    setHiddenFields({
      pageName: document.title,
      pageURI: window.location.href,
      pageID: window.location.pathname,
      ipAddress: visitorIp,
      hutk,
    })
  }, [visitorIp])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || status === 'submitting') return
    setStatus('submitting')
    // Gated — success only shows once HubSpot actually accepts the submission,
    // so a rejected one is never reported to the visitor as "Subscribed".
    const ok = await submitToHubspot(email, visitorIp)
    setStatus(ok ? 'success' : 'error')
  }

  const hidden = (
    <>
      {status === 'error' && (
        <p
          role="alert"
          style={{
            flexBasis: '100%',
            margin: 0,
            fontFamily: 'var(--seasons-font-polysans)',
            fontSize: 'var(--seasons-font-size-14)',
            color: 'var(--seasons-color-danger-600)',
          }}
        >
          Something went wrong. Please try again.
        </p>
      )}
      <input type="hidden" name="collateral_title" value={hiddenFields.pageName} />
      <input type="hidden" name="collateral"       value={hiddenFields.pageURI} />
      <input type="hidden" name="collateral_type"  value={hiddenFields.pageID} />
      <input type="hidden" name="ipAddress"        value={hiddenFields.ipAddress} />
      <input type="hidden" name="hutk"             value={hiddenFields.hutk} />
    </>
  )

  if (v === 'midnight-statement') return <MidnightStatement heading={heading} body={body} email={email} setEmail={setEmail} status={status} handleSubmit={handleSubmit} hidden={hidden} />
  if (v === 'split-panel') return <SplitPanel heading={heading} body={body} email={email} setEmail={setEmail} status={status} handleSubmit={handleSubmit} hidden={hidden} />
  if (v === 'minimal-eyebrow') return <MinimalEyebrow heading={heading} body={body} email={email} setEmail={setEmail} status={status} handleSubmit={handleSubmit} hidden={hidden} />
  if (v === 'inline-ribbon') return <InlineRibbon heading={heading} body={body} email={email} setEmail={setEmail} status={status} handleSubmit={handleSubmit} hidden={hidden} />
  if (v === 'compact-card') return <CompactCard heading={heading} body={body} email={email} setEmail={setEmail} status={status} handleSubmit={handleSubmit} hidden={hidden} />
  return null
}

interface VariantProps {
  heading: string
  body: string
  email: string
  setEmail: (v: string) => void
  status: 'idle' | 'submitting' | 'success' | 'error'
  handleSubmit: (e: React.FormEvent) => void
  hidden: React.ReactNode
}

// ──────────────────────────────────────────────────────────────────────────────
// V1 — Midnight statement
// Big display heading on midnight surface, primary button. Conversion moment.
function MidnightStatement({ heading, body, email, setEmail, status, handleSubmit, hidden }: VariantProps) {
  return (
    <div
      style={{
        background: 'var(--seasons-color-midnight)',
        borderRadius: 'var(--seasons-radius-card-lg)',
        padding: 'var(--seasons-space-12) var(--seasons-space-10)',
        maxWidth: '720px',
        textAlign: 'center',
      }}
    >
      <h3
        className="type-display-md"
        style={{ color: 'var(--seasons-color-on-dark-heading)', textWrap: 'balance', margin: 0 }}
      >
        {heading}
      </h3>
      <p
        style={{
          marginTop: 'var(--seasons-space-4)',
          fontFamily: 'var(--seasons-font-polysans)',
          fontSize: 'var(--seasons-font-size-18)',
          lineHeight: 'var(--seasons-line-height-relaxed)',
          color: 'var(--seasons-color-on-dark-body)',
          textWrap: 'pretty',
        }}
      >
        {body}
      </p>
      {status === 'success' ? (
        <p style={{ marginTop: 'var(--seasons-space-6)', color: 'var(--seasons-color-on-dark-heading)', fontFamily: 'var(--seasons-font-polysans)', fontSize: 'var(--seasons-font-size-16)' }}>
          Subscribed. Check your inbox to confirm.
        </p>
      ) : (
        <form
          onSubmit={handleSubmit}
          style={{
            marginTop: 'var(--seasons-space-7)',
            display: 'flex',
            gap: 'var(--seasons-space-2)',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {hidden}
          <div className="input-root blog-newsletter-input" data-dark style={{ flex: '1 1 280px', minWidth: 0 }}>
            <div className="input-container">
              <input
                className="input-field"
                type="email"
                required
                placeholder="Work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Work email"
              />
            </div>
          </div>
          <button type="submit" className="block-button-link">
            <div className="block-button block-button--md block-button--primary">
              <div className="block-button__text">
                <span className="btn-primary-text">{status === 'submitting' ? 'Subscribing…' : 'Subscribe'}</span>
                <span className="btn-hover-text">{status === 'submitting' ? 'Subscribing…' : 'Subscribe'}</span>
              </div>
            </div>
          </button>
        </form>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// V2 — Split panel
// 2-col: left midnight panel with heading + body, right ivory panel with form.
function SplitPanel({ heading, body, email, setEmail, status, handleSubmit, hidden }: VariantProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        borderRadius: 'var(--seasons-radius-card-lg)',
        overflow: 'hidden',
        maxWidth: '880px',
        boxShadow: 'var(--seasons-shadow-md)',
      }}
    >
      <div
        style={{
          background: 'var(--seasons-color-midnight)',
          padding: 'var(--seasons-space-10) var(--seasons-space-8)',
          color: 'var(--seasons-color-on-dark-heading)',
        }}
      >
        <h3 className="type-display-md" style={{ margin: 0, color: 'var(--seasons-color-on-dark-heading)', textWrap: 'balance' }}>
          {heading}
        </h3>
        <p
          style={{
            marginTop: 'var(--seasons-space-4)',
            fontFamily: 'var(--seasons-font-polysans)',
            fontSize: 'var(--seasons-font-size-16)',
            lineHeight: 'var(--seasons-line-height-relaxed)',
            color: 'var(--seasons-color-on-dark-body)',
          }}
        >
          {body}
        </p>
      </div>
      <div
        style={{
          background: 'var(--seasons-color-surface-subtle)',
          padding: 'var(--seasons-space-10) var(--seasons-space-8)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {status === 'success' ? (
          <p style={{ color: 'var(--seasons-color-prose-heading)', fontFamily: 'var(--seasons-font-polysans)' }}>
            Subscribed. Check your inbox to confirm.
          </p>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--seasons-space-3)' }}>
            {hidden}
            <label
              htmlFor="ns-split-email"
              style={{
                fontFamily: 'var(--seasons-font-polysans)',
                fontSize: 'var(--seasons-font-size-13)',
                fontWeight: 'var(--seasons-font-weight-medium)',
                color: 'var(--seasons-color-text-secondary)',
              }}
            >
              Work email
            </label>
            <div className="input-root blog-newsletter-input">
              <div className="input-container">
                <input
                  id="ns-split-email"
                  className="input-field"
                  type="email"
                  required
                  placeholder="you@hospital.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Work email"
                />
              </div>
            </div>
            <button type="submit" className="block-button-link" style={{ marginTop: 'var(--seasons-space-2)' }}>
              <div className="block-button block-button--md block-button--midnight">
                <div className="block-button__text">
                  <span className="btn-primary-text">{status === 'submitting' ? 'Subscribing…' : 'Subscribe'}</span>
                  <span className="btn-hover-text">{status === 'submitting' ? 'Subscribing…' : 'Subscribe'}</span>
                </div>
              </div>
            </button>
            <p
              style={{
                marginTop: 'var(--seasons-space-2)',
                fontFamily: 'var(--seasons-font-polysans)',
                fontSize: 'var(--seasons-font-size-12)',
                color: 'var(--seasons-color-text-tertiary)',
              }}
            >
              We respect your inbox. Unsubscribe anytime.
            </p>
          </form>
        )}
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// V4 — Minimal eyebrow
// No card; section-x-pad, eyebrow + dot + slim heading + form, all centered.
function MinimalEyebrow({ heading, body, email, setEmail, status, handleSubmit, hidden }: VariantProps) {
  return (
    <div style={{ maxWidth: '640px', textAlign: 'center' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--seasons-space-2)',
          marginBottom: 'var(--seasons-space-4)',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
          <circle cx="8" cy="8" r="7" stroke="var(--seasons-color-cyber-blue)" strokeOpacity="0.25" strokeWidth="1.5" />
          <circle cx="8" cy="8" r="4" fill="var(--seasons-color-cyber-blue)" />
        </svg>
        <p className="type-eyebrow-lg" style={{ color: 'var(--seasons-color-text-brand)', margin: 0 }}>
          Newsletter
        </p>
      </div>
      <h3
        className="type-display-md"
        style={{
          color: 'var(--seasons-color-prose-heading)',
          fontWeight: 'var(--seasons-font-weight-light)',
          textWrap: 'balance',
          margin: 0,
        }}
      >
        {heading}
      </h3>
      <p
        style={{
          marginTop: 'var(--seasons-space-4)',
          fontFamily: 'var(--seasons-font-polysans)',
          fontSize: 'var(--seasons-font-size-18)',
          lineHeight: 'var(--seasons-line-height-relaxed)',
          color: 'var(--seasons-color-prose-body)',
        }}
      >
        {body}
      </p>
      {status === 'success' ? (
        <p style={{ marginTop: 'var(--seasons-space-6)', color: 'var(--seasons-color-prose-heading)', fontFamily: 'var(--seasons-font-polysans)' }}>
          Subscribed. Check your inbox to confirm.
        </p>
      ) : (
        <form
          onSubmit={handleSubmit}
          style={{
            marginTop: 'var(--seasons-space-7)',
            display: 'flex',
            gap: 'var(--seasons-space-2)',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {hidden}
          <div className="input-root blog-newsletter-input" style={{ flex: '1 1 280px', minWidth: 0 }}>
            <div className="input-container" style={{ borderRadius: 'var(--seasons-radius-full)' }}>
              <input
                className="input-field"
                type="email"
                required
                placeholder="Work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Work email"
              />
            </div>
          </div>
          <button type="submit" className="block-button-link">
            <div className="block-button block-button--md block-button--midnight">
              <div className="block-button__text">
                <span className="btn-primary-text">{status === 'submitting' ? 'Subscribing…' : 'Subscribe'}</span>
                <span className="btn-hover-text">{status === 'submitting' ? 'Subscribing…' : 'Subscribe'}</span>
              </div>
            </div>
          </button>
        </form>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// V5 — Inline ribbon
// Single row: heading + body inline, email field, arrow icon button. No card.
function InlineRibbon({ heading, body, email, setEmail, status, handleSubmit, hidden }: VariantProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--seasons-space-8)',
        padding: 'var(--seasons-space-6) var(--seasons-space-8)',
        borderTop: 'var(--seasons-border-width-default) solid var(--seasons-color-border-default)',
        borderBottom: 'var(--seasons-border-width-default) solid var(--seasons-color-border-default)',
        flexWrap: 'wrap',
        background: 'var(--seasons-color-surface-default)',
        maxWidth: '1080px',
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <h3
          className="type-heading-md"
          style={{
            color: 'var(--seasons-color-prose-heading)',
            margin: 0,
            fontWeight: 'var(--seasons-font-weight-medium)',
          }}
        >
          {heading}
        </h3>
        <p
          style={{
            marginTop: 'var(--seasons-space-1)',
            fontFamily: 'var(--seasons-font-polysans)',
            fontSize: 'var(--seasons-font-size-14)',
            color: 'var(--seasons-color-prose-meta)',
            lineHeight: 'var(--seasons-line-height-snug)',
          }}
        >
          {body}
        </p>
      </div>
      {status === 'success' ? (
        <p style={{ color: 'var(--seasons-color-prose-heading)', fontFamily: 'var(--seasons-font-polysans)', fontSize: 'var(--seasons-font-size-14)' }}>
          Subscribed. Check your inbox.
        </p>
      ) : (
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--seasons-space-2)',
            flex: '1 1 320px',
            justifyContent: 'flex-end',
          }}
        >
          {hidden}
          <div className="input-root blog-newsletter-input" style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div className="input-container">
              <input
                className="input-field"
                type="email"
                required
                placeholder="Work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Work email"
              />
            </div>
          </div>
          <button
            type="submit"
            aria-label="Subscribe"
            className="btn-icon-only btn-icon-only--md btn-icon-only--midnight"
          >
            <ArrowRight size={18} weight="bold" />
          </button>
        </form>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// V6 — Compact card
// surface-subtle card, single column, inline input + arrow icon button, disclaimer.
function CompactCard({ heading, body, email, setEmail, status, handleSubmit, hidden }: VariantProps) {
  return (
    <div
      style={{
        background: 'var(--seasons-color-surface-subtle)',
        border: 'var(--seasons-border-width-default) solid var(--seasons-color-border-default)',
        borderRadius: 'var(--seasons-radius-card-lg)',
        padding: 'var(--seasons-space-10)',
      }}
    >
      <h3
        className="type-heading-xl"
        style={{
          marginTop: 'var(--seasons-space-2)',
          maxWidth: '80%',
          color: 'var(--seasons-color-prose-heading)',
          fontWeight: 'var(--seasons-font-weight-light)',
          textWrap: 'balance',
        }}
      >
        {heading}
      </h3>
      <p
        style={{
          marginTop: 'var(--seasons-space-3)',
          maxWidth: '80%',
          fontFamily: 'var(--seasons-font-polysans)',
          fontSize: 'var(--seasons-font-size-16)',
          lineHeight: 'var(--seasons-line-height-relaxed)',
          color: 'var(--seasons-color-prose-body)',
        }}
      >
        {body}
      </p>
      {status === 'success' ? (
        <SubscribeSuccess />
      ) : (
        <form onSubmit={handleSubmit} style={{ marginTop: 'var(--seasons-space-6)' }}>
          {hidden}
          <InputSubmit
            id="ns-compact-email"
            label="Enter your email ID"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            loading={status === 'submitting'}
            submitLabel="Subscribe"
          />
          <p
            style={{
              marginTop: 'var(--seasons-space-3)',
              maxWidth: '80%',
              fontFamily: 'var(--seasons-font-polysans)',
              fontSize: 'var(--seasons-font-size-13)',
              lineHeight: 'var(--seasons-line-height-relaxed)',
              color: 'var(--seasons-color-text-tertiary)',
            }}
          >
            Please provide your email address if you'd like to receive our monthly newsletter. You can unsubscribe at any time.
          </p>
        </form>
      )}
    </div>
  )
}
